/**
 * BriefRailSection — customer-fidelity Brief Rail summary.
 *
 * Phase 8c customer surface:
 *   has_brief, latest_revision, latest_status, brief_id, sealed_at,
 *   superseded, bridged_count, card_states[], has_share_link
 *
 * We render a calm one-card summary. No revision history. No worker
 * attribution. No delivery_log_summary (admin-only). No raw share-token
 * value (the boolean is enough).
 *
 * The card_states list is intentionally not surfaced as a separate
 * grid — the existing Event Command Center already exposes vendor
 * cards; this section just summarizes "how many recommendations have
 * you started conversations with?"
 */

import React from 'react';
import { FileText, Link2, ChevronRight } from 'lucide-react';

export default function BriefRailSection({ rail, eventId }) {
  if (!rail || !rail.has_brief) return null;
  const status = rail.latest_status;
  const sealed = status === 'ready';
  const cards = Array.isArray(rail.card_states) ? rail.card_states : [];
  const bridged = Number(rail.bridged_count || 0);
  const total = cards.length || 0;
  const hasShareLink = !!rail.has_share_link;

  let statusLine;
  if (sealed && rail.superseded) {
    statusLine = 'A newer brief is on the way';
  } else if (sealed) {
    statusLine = `Brief ready · revision ${rail.latest_revision || 1}`;
  } else {
    statusLine = 'Your concierge is still working on it';
  }

  return (
    <div
      className="rounded-xl bg-white border border-gray-100 p-4"
      data-testid="ops-brief-rail"
    >
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4 text-violet-500" aria-hidden />
        <p className="text-xs uppercase tracking-wide text-gray-400">
          Concierge brief
        </p>
      </div>
      <p
        className="text-sm font-medium text-gray-900"
        data-testid="ops-brief-status-line"
      >
        {statusLine}
      </p>

      {sealed && total > 0 && (
        <p className="text-xs text-gray-500 mt-1" data-testid="ops-brief-bridged-count">
          {bridged === 0
            ? `${total} recommendation${total === 1 ? '' : 's'} waiting for you`
            : `${bridged} of ${total} recommendation${total === 1 ? '' : 's'} in conversation`}
        </p>
      )}

      {sealed && cards.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {cards.slice(0, 4).map((c, idx) => (
            <li
              key={`${c.category || 'cat'}-${c.bucket || 'b'}-${idx}`}
              className="flex items-center justify-between text-xs gap-2"
              data-testid={`ops-brief-card-${c.bucket || 'top_pick'}-${idx}`}
            >
              <span className="text-gray-700 truncate">
                {c.vendor_name || 'A recommendation'}
                {c.bucket && c.bucket !== 'top_pick' && (
                  <span className="text-gray-400"> · {c.bucket.replace('_', ' ')}</span>
                )}
              </span>
              <span
                className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded-full ${
                  c.state === 'bridged'
                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                    : 'bg-gray-50 text-gray-500 ring-1 ring-gray-200'
                }`}
              >
                {c.state === 'bridged' ? 'In conversation' : 'Waiting'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {hasShareLink && (
        <p
          className="mt-3 text-xs text-gray-500 inline-flex items-center gap-1"
          data-testid="ops-brief-share-link-indicator"
        >
          <Link2 className="w-3.5 h-3.5 text-gray-400" aria-hidden />
          Shareable link is live
          <ChevronRight className="w-3 h-3 text-gray-300" aria-hidden />
        </p>
      )}

      {/* Phase 9A — customer "Open Brief" action.
          Only surfaced when the latest brief is sealed AND we have an
          event id to route with. Plain anchor — the Brief Viewer is a
          destination route, a full SPA hop is acceptable here and
          avoids coupling this section to a router library. */}
      {sealed && eventId && (
        <a
          href={`/events/${eventId}/brief`}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-violet-600 hover:text-violet-700"
          data-testid="ops-brief-open-link"
        >
          Open Brief
          <ChevronRight className="w-4 h-4" aria-hidden />
        </a>
      )}
    </div>
  );
}
